/**
 * API Client for calling backend endpoints.
 *
 * Usage:
 *   import { api, createAuthenticatedApiClient } from '@/lib/api-client';
 *
 *   // Public requests
 *   const data = await api.get('/api/items');
 *
 *   // Authenticated requests (requires wallet connection)
 *   const authApi = createAuthenticatedApiClient(token, walletAddress);
 *   const data = await authApi.post('/api/items', { name: 'New Item' });
 */

import { getPoofAPIHeaders, getPoofAPIUrl } from './config';

function getApiBaseUrl(path?: string): string {
  return getPoofAPIUrl(path);
}

interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: { code: string; message: string; details?: any };
  timestamp: number;
  requestId?: string;
}

export class ApiRequestError extends Error {
  status?: number;
  code?: string;
  details?: any;
  requestId?: string;

  constructor(message: string, options: { status?: number; code?: string; details?: any; requestId?: string } = {}) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = options.status;
    this.code = options.code;
    this.details = options.details;
    this.requestId = options.requestId;
  }
}

class ApiClient {
  constructor(private token?: string, private walletAddress?: string) {}

  private getDefaultHeaders(): Record<string, string> {
    return {
      'Content-Type': 'application/json',
      ...getPoofAPIHeaders(),
    };
  }

  private async request<T = any>(method: string, path: string, body?: any): Promise<T> {
    const headers: Record<string, string> = { ...this.getDefaultHeaders() };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }
    if (this.walletAddress) {
      headers['X-Wallet-Address'] = this.walletAddress;
    }

    let response: Response;
    try {
      response = await fetch(getApiBaseUrl(path), {
        method,
        headers,
        ...(body ? { body: JSON.stringify(body) } : {}),
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Network request failed';
      throw new ApiRequestError(`Network error: ${message}`);
    }

    const raw = await response.text();
    let json: ApiResponse<T> | null = null;
    if (raw) {
      try {
        json = JSON.parse(raw) as ApiResponse<T>;
      } catch {
        json = null;
      }
    }

    if (!response.ok) {
      throw new ApiRequestError(
        json?.error?.message || raw || `API error: ${response.status}`,
        {
          status: response.status,
          code: json?.error?.code,
          details: json?.error?.details,
          requestId: json?.requestId,
        }
      );
    }

    if (!json) {
      throw new ApiRequestError('API returned an invalid response', { status: response.status });
    }

    if (!json.success) {
      throw new ApiRequestError(
        json.error?.message || `API error: ${response.status}`,
        {
          status: response.status,
          code: json.error?.code,
          details: json.error?.details,
          requestId: json.requestId,
        }
      );
    }

    return json.data as T;
  }

  async get<T = any>(path: string): Promise<T> {
    return this.request<T>('GET', path);
  }

  async post<T = any>(path: string, body?: any): Promise<T> {
    return this.request<T>('POST', path, body);
  }

  async put<T = any>(path: string, body?: any): Promise<T> {
    return this.request<T>('PUT', path, body);
  }

  async delete<T = any>(path: string): Promise<T> {
    return this.request<T>('DELETE', path);
  }

  async patch<T = any>(path: string, body?: any): Promise<T> {
    return this.request<T>('PATCH', path, body);
  }
}

/** Public API client (no auth) */
export const api = new ApiClient();

/**
 * Create an authenticated API client.
 * @param token - JWT token from wallet auth
 * @param walletAddress - User's wallet address
 */
export function createAuthenticatedApiClient(token: string, walletAddress: string): ApiClient {
  return new ApiClient(token, walletAddress);
}

export default api;
